import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MessageCircle, PenSquare } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAirline } from '../../utils/airlines';
import { ConversationRow } from './ConversationRow';
import { ChatView } from './ChatView';
import { GroupChatView } from './GroupChatView';
import { AvatarImg } from '../shared/Avatar';
import type { ConversationItem, User } from '../../types';

export function MessagesTab() {
  const {
    messages, currentUser, usersById, friends,
    groupChats, groupMembers, groupMessages, conversationDeletions,
    softDeleteDmConversation, softDeleteGroupChat,
  } = useApp();
  const [openChat, setOpenChat] = useState<{ type: 'dm'; partnerId: string; prefill?: string } | { type: 'group'; groupId: string } | null>(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const originRef = useRef<string | null>(null);

  // Handle ?chat=<userId>&prefill=<text> and ?groupChat=<groupId> deep links
  useEffect(() => {
    const chatId = searchParams.get('chat');
    const groupChatId = searchParams.get('groupChat');
    const origin = searchParams.get('origin');
    if (origin) originRef.current = origin;
    if (chatId && usersById.has(chatId)) {
      const prefill = searchParams.get('prefill') || undefined;
      setOpenChat({ type: 'dm', partnerId: chatId, prefill });
      setSearchParams({}, { replace: true });
    } else if (groupChatId && groupChats.some((gc) => gc.id === groupChatId)) {
      // Only open if user has active membership
      const hasActiveMembership = groupMembers.some(
        (m) => m.groupId === groupChatId && m.userId === currentUser.id && !m.deletedAt,
      );
      if (hasActiveMembership) {
        setOpenChat({ type: 'group', groupId: groupChatId });
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, usersById, groupChats]);

  // Build unified conversation list
  const conversationItems = useMemo((): ConversationItem[] => {
    const items: ConversationItem[] = [];

    // 1. DM conversations
    const dmMap = new Map<string, typeof messages>();
    for (const msg of messages) {
      const partnerId = msg.senderId === currentUser.id ? msg.receiverId : msg.senderId;
      const existing = dmMap.get(partnerId);
      if (existing) existing.push(msg);
      else dmMap.set(partnerId, [msg]);
    }

    // Build deletion lookup for quick access
    const deletionMap = new Map<string, string>(); // partnerId → deletedAt
    for (const d of conversationDeletions) {
      deletionMap.set(d.partnerId, d.deletedAt);
    }

    for (const [partnerId, partnerMsgs] of dmMap) {
      const partner = usersById.get(partnerId);
      if (!partner) continue;

      // Filter out messages before soft-delete timestamp
      const deletedAt = deletionMap.get(partnerId);
      const visibleMsgs = deletedAt
        ? partnerMsgs.filter((m) => new Date(m.createdAt) > new Date(deletedAt))
        : partnerMsgs;

      if (visibleMsgs.length === 0) continue;

      const sorted = visibleMsgs.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      const lastMessage = sorted[sorted.length - 1];
      const unreadCount = sorted.filter(
        (m) => m.senderId === partnerId && m.receiverId === currentUser.id && !m.readAt,
      ).length;

      items.push({ type: 'dm', partnerId, partner, lastMessage, unreadCount });
    }

    // 2. Group conversations
    for (const gc of groupChats) {
      const myMembership = groupMembers.find(
        (gm) => gm.groupId === gc.id && gm.userId === currentUser.id,
      );
      if (!myMembership) continue;

      // Filter messages: only those after soft-delete, if applicable
      const gcMsgs = groupMessages.filter((m) => {
        if (m.groupId !== gc.id) return false;
        if (myMembership.deletedAt && new Date(m.createdAt) <= new Date(myMembership.deletedAt)) return false;
        return true;
      });

      if (gcMsgs.length === 0) continue;

      const sorted = gcMsgs.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      const lastMessage = sorted[sorted.length - 1];

      // Unread: messages after last read timestamp (localStorage)
      const lastRead = localStorage.getItem(`rostfound_group_read_${gc.id}`);
      const unreadCount = lastRead
        ? sorted.filter((m) => m.senderId !== currentUser.id && new Date(m.createdAt) > new Date(lastRead)).length
        : sorted.filter((m) => m.senderId !== currentUser.id).length;

      // Get member users (excluding self for display, but include for count)
      const memberIds = groupMembers
        .filter((gm) => gm.groupId === gc.id)
        .map((gm) => gm.userId);
      const memberUsersFiltered = memberIds
        .filter((id) => id !== currentUser.id)
        .map((id) => usersById.get(id))
        .filter(Boolean) as User[];

      items.push({ type: 'group', groupId: gc.id, group: gc, members: memberUsersFiltered, lastMessage, unreadCount });
    }

    // Sort by most recent activity
    items.sort((a, b) => {
      const aTime = new Date(a.lastMessage.createdAt).getTime();
      const bTime = new Date(b.lastMessage.createdAt).getTime();
      return bTime - aTime;
    });

    return items;
  }, [messages, currentUser.id, usersById, groupChats, groupMembers, groupMessages, conversationDeletions]);

  // Friends without existing conversations (for new message picker)
  const conversationPartnerIds = useMemo(
    () => new Set(conversationItems.filter((c) => c.type === 'dm').map((c) => (c as { partnerId: string }).partnerId)),
    [conversationItems],
  );

  return (
    <div className="flex flex-col h-full bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
        <h2 className="text-lg font-bold text-slate-100">Messages</h2>
        {friends.length > 0 && (
          <button
            onClick={() => setShowNewMessage(true)}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-accent active:scale-95 transition-all"
            title="New message"
          >
            <PenSquare size={20} />
          </button>
        )}
      </div>

      {/* Conversation list */}
      {conversationItems.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700/50 flex items-center justify-center mb-4">
            <MessageCircle size={28} className="text-slate-500" />
          </div>
          <p className="text-slate-400 text-sm font-medium">No messages yet</p>
          <p className="text-slate-500 text-xs mt-1.5 max-w-[240px]">
            Tap the chat icon on a friend's card to start a conversation
          </p>
          {friends.length > 0 && (
            <button
              onClick={() => setShowNewMessage(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold active:scale-[0.97] transition-all"
            >
              <PenSquare size={16} />
              New message
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {conversationItems.map((item) => (
            <ConversationRow
              key={item.type === 'dm' ? item.partnerId : item.groupId}
              item={item}
              onClick={() => {
                if (item.type === 'dm') setOpenChat({ type: 'dm', partnerId: item.partnerId });
                else setOpenChat({ type: 'group', groupId: item.groupId });
              }}
              onDelete={() => {
                if (item.type === 'dm') softDeleteDmConversation(item.partnerId);
                else softDeleteGroupChat(item.groupId);
              }}
            />
          ))}
        </div>
      )}

      {/* New message friend picker */}
      {showNewMessage && (
        <FriendPicker
          friends={friends}
          existingPartnerIds={conversationPartnerIds}
          onSelect={(friend) => {
            setShowNewMessage(false);
            setOpenChat({ type: 'dm', partnerId: friend.id });
          }}
          onClose={() => setShowNewMessage(false)}
        />
      )}

      {/* Chat view overlay */}
      {openChat?.type === 'dm' && (
        <ChatView
          partnerId={openChat.partnerId}
          prefill={openChat.prefill}
          onClose={() => setOpenChat(null)}
        />
      )}
      {openChat?.type === 'group' && (
        <GroupChatView
          groupId={openChat.groupId}
          onClose={() => {
            setOpenChat(null);
            if (originRef.current === 'calendar') {
              originRef.current = null;
              navigate('/');
            }
          }}
        />
      )}
    </div>
  );
}

function FriendPicker({
  friends,
  existingPartnerIds,
  onSelect,
  onClose,
}: {
  friends: User[];
  existingPartnerIds: Set<string>;
  onSelect: (friend: User) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return friends.filter((f) => {
      if (q && !f.name.toLowerCase().includes(q) && !f.handle.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [friends, search]);

  // Sort: friends without conversations first
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aHas = existingPartnerIds.has(a.id) ? 1 : 0;
      const bHas = existingPartnerIds.has(b.id) ? 1 : 0;
      return aHas - bHas || a.name.localeCompare(b.name);
    });
  }, [filtered, existingPartnerIds]);

  return (
    <div className="absolute inset-0 z-[90] flex flex-col bg-slate-900" style={{ position: 'absolute' }}>
      {/* Header */}
      <div className="shrink-0 bg-slate-800 border-b border-slate-700/50 px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-slate-100">New Message</h3>
          <button
            onClick={onClose}
            className="text-sm text-slate-400 hover:text-slate-200 transition-colors px-2 py-1"
          >
            Cancel
          </button>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search friends..."
          autoFocus
          className="w-full bg-slate-700 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-accent/50"
        />
      </div>

      {/* Friend list */}
      <div className="flex-1 overflow-y-auto">
        {sorted.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
            No friends found
          </div>
        ) : (
          sorted.map((friend) => {
            const airline = getAirline(friend.airlineCode);
            return (
              <button
                key={friend.id}
                onClick={() => onSelect(friend)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 active:bg-slate-700/50 transition-colors text-left"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 overflow-hidden"
                  style={{ backgroundColor: airline.color, color: airline.textOnColor }}
                >
                  <AvatarImg src={friend.avatarUrl} name={friend.name} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-100">{friend.name}</div>
                  <div className="text-xs text-slate-500">@{friend.handle}</div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
